// ─────────────────────────────────────────────────────────────
// SRAM / RockShox model code parser
// Parses codes like FS-PIKE-SELP-B4, RS-SDLX-ULT-A2 etc.
// and portal URL abbreviated codes like fs-lyrk-ult-c2
// Returns product details without requiring any API call.
//
// Source: 2026 RockShox Spare Parts Catalog (Rev B, April 2026)
// ─────────────────────────────────────────────────────────────

// Generation code → approximate model year
// Letter = generation series; number = iteration within that series
const GEN_YEAR = {
  A1:'2015', A2:'2016', A3:'2017', A4:'2018', A5:'2019',
  A6:'2019', A7:'2019', A8:'2020', A9:'2021',
  B1:'2021', B2:'2022', B3:'2023', B4:'2024', B5:'2025',
  C1:'2021', C2:'2022', C3:'2023', C4:'2024',
  D1:'2022', D2:'2023', D3:'2024',
  E1:'2024', E2:'2025',
}

// Component type prefix → category
const TYPE_MAP = {
  FS: 'fork',
  FK: 'fork',
  RS: 'shock',
  SP: 'dropper',
  DP: 'dropper',
  RV: 'dropper',
}

// ─────────────────────────────────────────────────────────────
// MODEL_MAP: maps every abbreviated model segment from the
// SRAM portal URL / model code to the full product name.
// Verified against 2026 RockShox Spare Parts Catalog.
// ─────────────────────────────────────────────────────────────
const MODEL_MAP = {

  // ── SUSPENSION FORKS ─────────────────────────────────────

  // SID (multiple stanchion diameters)
  SID:    { name: 'SID',        travel: '100–120mm',  type: 'fork' },
  SIDS:   { name: 'SID SL',     travel: '100–120mm',  type: 'fork' },  // SID SL 32mm

  // Revelation
  RVL:    { name: 'Revelation', travel: '120–160mm',  type: 'fork' },

  // Pike
  PIKE:   { name: 'Pike',       travel: '120–160mm',  type: 'fork' },

  // Lyrik  — portal uses LYRK; full codes sometimes use LYRIK
  LYRK:   { name: 'Lyrik',      travel: '140–180mm',  type: 'fork' },
  LYRIK:  { name: 'Lyrik',      travel: '140–180mm',  type: 'fork' },
  LRYK:   { name: 'Lyrik',      travel: '140–180mm',  type: 'fork' },  // legacy typo variant

  // ZEB
  ZEB:    { name: 'ZEB',        travel: '150–190mm',  type: 'fork' },

  // BoXXer (dual-crown DH)
  BXR:    { name: 'BoXXer',     travel: '180–203mm',  type: 'fork' },
  BOXXER: { name: 'BoXXer',     travel: '180–203mm',  type: 'fork' },

  // Yari
  YARI:   { name: 'Yari',       travel: '120–160mm',  type: 'fork' },

  // Domain (single-crown + dual-crown)
  DOMN:   { name: 'Domain',     travel: '180–200mm',  type: 'fork' },
  DOMAIN: { name: 'Domain',     travel: '180–200mm',  type: 'fork' },
  DDC:    { name: 'Domain Dual Crown', travel: '180–203mm', type: 'fork' },

  // Recon
  RCN:    { name: 'Recon',      travel: '100–130mm',  type: 'fork' },   // Recon (basic)
  RCNS:   { name: 'Recon Silver', travel: '100–130mm', type: 'fork' },
  RCNG:   { name: 'Recon Gold', travel: '100–130mm',  type: 'fork' },
  RECON:  { name: 'Recon',      travel: '100–130mm',  type: 'fork' },

  // Judy
  JDY:    { name: 'Judy',       travel: '80–120mm',   type: 'fork' },   // Judy TK B1
  JDYS:   { name: 'Judy Silver', travel: '80–120mm',  type: 'fork' },
  JDYG:   { name: 'Judy Gold',  travel: '80–120mm',   type: 'fork' },
  JUDY:   { name: 'Judy',       travel: '80–120mm',   type: 'fork' },

  // Reba
  REBA:   { name: 'Reba',       travel: '80–120mm',   type: 'fork' },

  // Rudy
  RUDY:   { name: 'Rudy',       travel: '40–50mm',    type: 'fork' },   // Rudy XPLR
  RDYX:   { name: 'Rudy XL',    travel: '40–50mm',    type: 'fork' },   // Rudy XL A1 (2026+)

  // RS-1
  RS1:    { name: 'RS-1',       travel: '100–120mm',  type: 'fork' },

  // XC / entry level
  XC30:   { name: 'XC30',       travel: '80–130mm',   type: 'fork' },
  XC32:   { name: 'XC32',       travel: '80–130mm',   type: 'fork' },

  // 30mm stanchion variants (portal codes: 30G = 30 Gold, 30S = 30 Silver)
  '30G':  { name: '30 Gold',    travel: '80–130mm',   type: 'fork' },
  '30S':  { name: '30 Silver',  travel: '80–130mm',   type: 'fork' },

  // 35mm stanchion variants
  '35G':  { name: '35 Gold',    travel: '100–160mm',  type: 'fork' },
  '35S':  { name: '35 Silver',  travel: '100–160mm',  type: 'fork' },

  // Paragon (portal uses PARG / PARS depending on spec)
  PARG:   { name: 'Paragon',    travel: '100–120mm',  type: 'fork' },
  PARS:   { name: 'Paragon',    travel: '100–120mm',  type: 'fork' },
  PARAGON:{ name: 'Paragon',    travel: '100–120mm',  type: 'fork' },

  // Sektor variants
  SKTR:   { name: 'Sektor RL',    travel: '100–160mm', type: 'fork' },
  SKTG:   { name: 'Sektor Gold',  travel: '100–160mm', type: 'fork' },
  SKTS:   { name: 'Sektor Silver', travel: '100–160mm', type: 'fork' },
  SKRG:   { name: 'Sektor Gold',  travel: '100–160mm', type: 'fork' },  // older variant code
  SEKTOR: { name: 'Sektor',       travel: '100–160mm', type: 'fork' },

  // Bluto (fat bike)
  BLTO:   { name: 'Bluto',      travel: '80–120mm',   type: 'fork' },
  BLUTO:  { name: 'Bluto',      travel: '80–120mm',   type: 'fork' },

  // Argyle (older DJ/street fork)
  ARGL:   { name: 'Argyle',     travel: '80–100mm',   type: 'fork' },
  ARGYLE: { name: 'Argyle',     travel: '80–100mm',   type: 'fork' },

  // Psylo (Gold / Silver — 2025+)
  PSYL:   { name: 'Psylo',      travel: '80–130mm',   type: 'fork' },
  PSYLO:  { name: 'Psylo',      travel: '80–130mm',   type: 'fork' },

  // ── REAR SHOCKS ──────────────────────────────────────────

  // Super Deluxe (air and coil)
  SDLX:   { name: 'Super Deluxe',      travel: null, type: 'shock' },
  SUPERDLX: { name: 'Super Deluxe',    travel: null, type: 'shock' },

  // Super Deluxe Coil
  SDLC:   { name: 'Super Deluxe Coil', travel: null, type: 'shock' },

  // Deluxe Air
  DLX:    { name: 'Deluxe',            travel: null, type: 'shock' },
  DELUXE: { name: 'Deluxe',            travel: null, type: 'shock' },

  // Deluxe Coil
  DLXC:   { name: 'Deluxe Coil',       travel: null, type: 'shock' },

  // Vivid Air (VIVC) and Vivid Coil (VIVD)
  VIVC:   { name: 'Vivid Air',         travel: null, type: 'shock' },
  VIVD:   { name: 'Vivid Coil',        travel: null, type: 'shock' },
  VIVA:   { name: 'Vivid Air',         travel: null, type: 'shock' },  // older code
  VIVID:  { name: 'Vivid',             travel: null, type: 'shock' },

  // SIDLuxe
  SIDL:   { name: 'SIDLuxe',           travel: null, type: 'shock' },
  SIDLUX: { name: 'SIDLuxe',           travel: null, type: 'shock' },

  // Monarch
  MNR:    { name: 'Monarch',           travel: null, type: 'shock' },
  MNRP:   { name: 'Monarch Plus',      travel: null, type: 'shock' },
  MONARCH:{ name: 'Monarch',           travel: null, type: 'shock' },

  // Kage
  KAGE:   { name: 'Kage',              travel: null, type: 'shock' },

  // Legacy shock names (older bikes)
  NUDE:   { name: 'Nude',              travel: null, type: 'shock' },
  INLINE: { name: 'Inline',            travel: null, type: 'shock' },

  // ── DROPPERS / SEATPOSTS ─────────────────────────────────

  // Reverb AXS
  RVB:    { name: 'Reverb',            travel: null, type: 'dropper' },
  RVBAXS: { name: 'Reverb AXS',        travel: null, type: 'dropper' },

  // Reverb (various portal codes)
  REVERB: { name: 'Reverb',            travel: null, type: 'dropper' },
  TRANSFER: { name: 'Transfer',        travel: null, type: 'dropper' },
}

// ─────────────────────────────────────────────────────────────
// SPEC_MAP: maps spec-level abbreviations to display names.
// Verified against 2026 RockShox Spare Parts Catalog.
// ─────────────────────────────────────────────────────────────
const SPEC_MAP = {
  // Current
  ULT:    'Ultimate',
  UFA:    'Ultimate Flight Attendant',
  SELP:   'Select Plus',
  SEL:    'Select',
  BSE:    'Base',

  // Older charger / damper designations
  RC:     'RC',
  RC2:    'RC2',
  RC3:    'RC3',
  RCT:    'RCT',
  RCT3:   'RCT3',
  RC2D:   'RC2 Dual',
  CHRC:   'Charger RC',

  // RL / RL variants
  RL:     'RL',
  RL3:    'RL3',
  RLC:    'RLC',
  RLR:    'RLR',
  RLT:    'RLT',
  RLDT:   'RLDT',

  // Damper-specific
  RT:     'RT',
  RT3:    'RT3',
  RTR:    'RTR',
  R:      'R',
  R2C:    'R2C',
  R2C2:   'R2C2',

  // Coil-specific
  RKTV:   'RockShox Transmitter',
  TKTV:   'Transmission',

  // Flight Attendant / UDH
  UDH:    'UDH',
  ULDH:   'Ultimate UDH',
  THRU:   'Thru',

  // SID-specific multi-damper suffixes (e.g. SID35mm codes)
  ULT2:   'Ultimate 2-Position',
  ULT3:   'Ultimate 3-Position',
  SEL2:   'Select 2-Position',
  SEL3:   'Select 3-Position',
  SLP2:   'Select Plus 2-Position',
  SLP3:   'Select Plus 3-Position',

  // SIDLuxe-specific
  ISFA:   'ISOSTRUT Flight Attendant',
  STFA:   'Flight Attendant',
  TKIS:   'ISOSTRUT',
  WCID:   'World Cup Internal Dropout',

  // Spec levels used on older models
  WC:     'World Cup',
  WCWC:   'World Cup',
  TEAM:   'Team',
  XX:     'XX',
  XXWC:   'XX World Cup',
  GOLD:   'Gold',
  SILVER: 'Silver',
  GR:     'Gold RC',
  GRC:    'Gold RC',
  GRCF:   'Gold RC (Frame-internal)',
  SR:     'Silver RC',
  SRC:    'Silver RC',
  SRCF:   'Silver RC (Frame-internal)',
  SRF:    'Silver R (Frame-internal)',
  SRF2:   'Silver R (Frame-internal)',

  // Reverb AXS portal
  AXS:    'AXS',
  XPLR:   'XPLR',
  STLH:   'Stealth',
  '1X':   '1x',
  ST1X:   'Stealth 1x',

  // Misc / legacy
  TK:     'TurnKey',
  SL:     'SL',
  DJ:     'Dirt Jump',
  AC:     'Air Can',
  SV:     'Solo Valve',
  SCTT:   'Scott',
  NUDE:   '',
  BNUL:   '',
  NONE:   '',
}

// ─────────────────────────────────────────────────────────────
// parseSramCode(code)
// Accepts full model codes (FS-PIKE-SELP-B4) and portal URL
// abbreviated codes (fs-lyrk-ult-c2).
// Returns an object or null if parsing fails.
// ─────────────────────────────────────────────────────────────
export function parseSramCode(code) {
  if (!code) return null
  const upper = code.toUpperCase().trim()
  const parts = upper.split('-').filter(Boolean)
  if (parts.length < 2) return null

  // Identify component type from first segment
  const typePrefix = parts[0]
  const prefixType = TYPE_MAP[typePrefix] || null

  // Find model (search all segments after prefix)
  let modelData = null
  let modelKey = null
  let modelIdx = -1

  for (let i = 1; i < parts.length; i++) {
    const seg = parts[i]

    // Direct lookup
    if (MODEL_MAP[seg]) {
      modelData = MODEL_MAP[seg]
      modelKey = seg
      modelIdx = i
      break
    }

    // Try numeric-prefixed segments like '30G', '35S' (split as ['30G'] — already handled above)
    // Also try compound: e.g. SUPER + DLX → not needed since we have SDLX / SUPERDLX
  }

  if (!modelData) return null

  // Determine component type: prefer model's own type, fall back to prefix
  const componentType = modelData.type || prefixType || 'fork'

  // Collect segments after the model key
  const afterModel = parts.slice(modelIdx + 1)

  let specName = ''
  let genCode = null

  for (const seg of afterModel) {
    // Generation code: single letter + 1-2 digits (e.g. B4, C2, A10)
    if (/^[A-E]\d{1,2}$/.test(seg)) {
      genCode = seg
      continue
    }
    // Spec level
    if (SPEC_MAP[seg] !== undefined && SPEC_MAP[seg] !== '') {
      specName = SPEC_MAP[seg]
    }
  }

  // Build display name
  const fullName = ['RockShox', modelData.name, specName].filter(Boolean).join(' ')

  // Year from generation code
  const year = genCode ? (GEN_YEAR[genCode] || null) : null

  return {
    model: fullName,
    type: componentType,
    year,
    travel: modelData.travel,
    part_number: upper,
    notes: [
      fullName,
      specName ? 'Spec: ' + specName : '',
      genCode ? 'Generation: ' + genCode : '',
      year ? 'Year: ~' + year : '',
      modelData.travel ? 'Travel range: ' + modelData.travel : '',
    ].filter(Boolean).join(' · ')
  }
}
